/**
 * Quiz Renderer Component
 * Renders questions in a scroll-snap carousel
 */

import { createLogger } from '../core/logger';
import {
  getState,
  setCurrentQuestion,
  recordAnswer,
  completeQuiz,
  startQuiz,
  saveProgress
} from '../core/state';
import { trackQuizProgress, trackQuizComplete } from '../services/pixels';
import { preloadNextQuestion } from '../services/preload';
import {
  trackQuizStarted,
  trackQuestionAnswered,
  trackQuizCompleted
} from '../services/progress';
import { updateSessionQuizStarted, updateSessionQuizCompleted } from '../services/api';
import { getOrCreateLeadId } from '../core/state';
import { showSmartPathModal } from './SmartPathModal';
import { FunnelConfig, Question, Answer, SmartPathPage } from '../types';
import { sendProgress, sendAnswer, onQuestionChange } from '../services/embed';

const logger = createLogger('QuizRenderer');

let container: HTMLElement | null = null;
let carousel: HTMLElement | null = null;
let questionStartTime: number = Date.now();
let quizRenderedAt: number = Date.now(); // For timeToFirstAnswer calculation

/**
 * Initialize quiz renderer
 */
export function initQuizRenderer(targetContainer: HTMLElement): void {
  container = targetContainer;
  logger.info('Quiz renderer initialized');
}

/**
 * Render the quiz
 */
export function renderQuiz(funnel: FunnelConfig): void {
  if (!container) {
    logger.error('Container not initialized');
    return;
  }

  // Track when quiz became interactive (for timeToFirstAnswer)
  quizRenderedAt = Date.now();

  const state = getState();
  logger.info('Rendering quiz', {
    questionCount: funnel.questions.length,
    currentIndex: state.quiz.currentQuestionIndex
  });

  // Determine if header should be collapsed (resuming past first question)
  const isCollapsed = state.quiz.currentQuestionIndex > 0;
  const showBranding = funnel.showBranding !== false;

  // Check if logo should show on first page
  const showLogo = funnel.customLogo && (funnel.logoSettings?.showOnFirstPage !== false);

  // Build HTML
  const html = `
    <div class="quiz-header ${isCollapsed ? 'is-collapsed' : ''}" id="quiz-header">
      ${showLogo ? `<img src="${escapeHtml(funnel.customLogo!)}" alt="" class="quiz-header-logo">` : ''}
      <h1 class="quiz-header-title">${escapeHtml(funnel.title)}</h1>
      ${funnel.subtitle ? `<p class="quiz-header-subtitle">${escapeHtml(funnel.subtitle)}</p>` : ''}
    </div>

    <div class="quiz-main">
      <div class="quiz-container">
        <div class="progress-bar">
          <div class="progress-fill"></div>
        </div>

        <div class="carousel" id="quiz-carousel">
          ${funnel.questions.map((q, i) => renderQuestion(q, i)).join('')}
        </div>
      </div>
    </div>

    ${showBranding ? `
      <footer class="branding-footer">
        <a href="https://www.1minutefunnel.com" target="_blank" rel="noopener" class="branding-link">
          Create Your Own @ 1MinuteFunnel.com
        </a>
      </footer>
    ` : ''}
  `;

  container.innerHTML = html;
  carousel = document.getElementById('quiz-carousel');

  // Attach event listeners
  attachEventListeners(funnel);

  // Lock backward scrolling - quiz is forward-only
  attachScrollLock();

  // Update progress
  updateProgress(state.quiz.currentQuestionIndex, funnel.questions.length);

  // Scroll to current question if resuming
  if (state.quiz.currentQuestionIndex > 0) {
    scrollToQuestion(state.quiz.currentQuestionIndex);
  }

  // Mark quiz as started
  if (!state.quiz.startedAt) {
    startQuiz();
    trackQuizStarted();
  }

  questionStartTime = Date.now();
  logger.info('Quiz rendered');
}

/**
 * Render a single question
 */
function renderQuestion(question: Question, index: number): string {
  const state = getState();
  const existingAnswer = state.quiz.answers[question.id];

  return `
    <div class="carousel-slide" data-question-index="${index}">
      <article class="question-card" data-question-id="${question.id}">
        ${question.image ? `
          <div class="question-image">
            <img src="${escapeHtml(question.image)}" alt="" loading="lazy">
          </div>
        ` : ''}
        ${question.video ? `
          <div class="question-video">
            <video src="${escapeHtml(question.video)}" controls playsinline></video>
          </div>
        ` : ''}
        <h2 class="question-text">${escapeHtml(question.question)}</h2>
        ${question.subtitle ? `<p class="question-subtitle">${escapeHtml(question.subtitle)}</p>` : ''}

        <div class="options">
          ${question.options.map((opt, optIndex) => {
            // Support both 'label' and 'text' properties (editor uses both for compatibility)
            const optionText = opt.label || opt.text || '';
            return `
            <input
              type="radio"
              name="q${index}"
              id="q${index}-opt${optIndex}"
              value="${optIndex}"
              class="option-radio"
              data-question-id="${question.id}"
              data-question-index="${index}"
              data-option-index="${optIndex}"
              data-option-value="${escapeHtml(opt.value)}"
              data-option-label="${escapeHtml(optionText)}"
              ${existingAnswer?.optionIndex === optIndex ? 'checked' : ''}>
            <label for="q${index}-opt${optIndex}" class="option-label">
              ${opt.emoji ? `<span class="option-emoji">${opt.emoji}</span>` : ''}
              ${opt.image ? `<img src="${escapeHtml(opt.image)}" alt="" class="option-image">` : ''}
              <span class="option-text">${escapeHtml(optionText)}</span>
            </label>
          `}).join('')}
        </div>
      </article>
    </div>
  `;
}

/**
 * Attach event listeners to options
 */
function attachEventListeners(funnel: FunnelConfig): void {
  const radios = container?.querySelectorAll('.option-radio');

  radios?.forEach(radio => {
    radio.addEventListener('change', (e) => {
      handleAnswerSelection(e.target as HTMLInputElement, funnel);
    });
  });

  logger.debug('Event listeners attached', { radioCount: radios?.length });
}

/**
 * Attach scroll lock to prevent backward navigation
 * Quiz is forward-only - users cannot swipe back to previous questions
 */
function attachScrollLock(): void {
  if (!carousel) return;

  let isScrolling = false;
  let scrollTimeout: ReturnType<typeof setTimeout>;

  carousel.addEventListener('scroll', () => {
    if (isScrolling) return;

    // Debounce scroll events
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const state = getState();
      const currentIndex = state.quiz.currentQuestionIndex;
      const slideWidth = carousel!.clientWidth;
      const currentScroll = carousel!.scrollLeft;
      const targetScroll = currentIndex * slideWidth;

      // If user scrolled backward from current question, snap back
      if (currentScroll < targetScroll - 10) {
        isScrolling = true;
        logger.debug('Blocking backward scroll', { currentScroll, targetScroll });

        carousel!.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });

        // Reset flag after scroll animation
        setTimeout(() => {
          isScrolling = false;
        }, 300);
      }
    }, 50);
  }, { passive: true });

  logger.debug('Scroll lock attached');
}

/**
 * Handle answer selection
 */
function handleAnswerSelection(radio: HTMLInputElement, funnel: FunnelConfig): void {
  const questionId = radio.dataset.questionId!;
  const questionIndex = parseInt(radio.dataset.questionIndex!, 10);
  const optionIndex = parseInt(radio.dataset.optionIndex!, 10);
  const optionValue = radio.dataset.optionValue!;
  const optionLabel = radio.dataset.optionLabel!;

  const timeSpent = Date.now() - questionStartTime;

  // Record answer
  const answer: Answer = {
    questionId,
    questionIndex,
    optionIndex,
    optionValue,
    optionLabel,
    timestamp: Date.now(),
    timeSpent
  };

  recordAnswer(answer);
  saveProgress();

  // On first answer, mark quiz started and link session to lead
  if (questionIndex === 0) {
    const state = getState();
    const leadId = getOrCreateLeadId();
    updateSessionQuizStarted(funnel.funnelID, state.session.sessionId, leadId, quizRenderedAt);
  }

  logger.info('Answer recorded', {
    questionIndex,
    optionIndex,
    timeSpent
  });

  // Track progress (pixels)
  trackQuizProgress(questionIndex, funnel.questions.length);

  // Track to server
  trackQuestionAnswered({
    questionId,
    questionIndex,
    optionIndex,
    optionValue,
    timeSpent
  });

  // Notify parent iframe (if embedded)
  sendAnswer(questionId, optionValue, optionLabel);
  sendProgress(questionIndex + 1, funnel.questions.length);

  // Preload next question's assets for better performance
  preloadNextQuestion(funnel, questionIndex);

  // Check for Smart Path trigger
  const smartPath = checkSmartPathTrigger(funnel, questionId, optionIndex);

  if (smartPath) {
    // Show Smart Path modal
    logger.info('Smart Path triggered', { pathId: smartPath.id, type: smartPath.type });

    showSmartPathModal(
      smartPath,
      { questionId, optionIndex },
      () => {
        // After Smart Path closes, continue the quiz flow
        advanceQuiz(funnel, questionIndex);
      }
    );
  } else {
    // No Smart Path - advance normally
    advanceQuiz(funnel, questionIndex);
  }
}

/**
 * Check if an answer triggers a Smart Path
 */
function checkSmartPathTrigger(
  funnel: FunnelConfig,
  questionId: string,
  optionIndex: number
): SmartPathPage | null {
  const bounceFlow = funnel.bounceFlow;

  // Check if bounce flow is enabled and has mappings
  if (!bounceFlow?.enabled || !bounceFlow.mappings) {
    return null;
  }

  // Check for a mapping for this question/option
  const questionMappings = bounceFlow.mappings[questionId];
  if (!questionMappings) {
    return null;
  }

  const pathId = questionMappings[optionIndex.toString()];
  if (!pathId) {
    return null;
  }

  // Find the page by ID
  const page = bounceFlow.pages.find(p => p.id === pathId);
  if (!page) {
    logger.warn('Smart Path page not found', { pathId });
    return null;
  }

  return page;
}

/**
 * Advance to next question or complete quiz
 */
function advanceQuiz(funnel: FunnelConfig, currentIndex: number): void {
  const nextIndex = currentIndex + 1;

  if (nextIndex < funnel.questions.length) {
    // Collapse header after first question
    if (currentIndex === 0) {
      const header = document.getElementById('quiz-header');
      const quizApp = document.querySelector('.quiz-app');
      logger.info('Collapsing header', { found: !!header });
      if (header) {
        // Disable hover effects during transition to prevent accidental highlights
        quizApp?.classList.add('is-transitioning');
        header.classList.add('is-collapsed');
        logger.debug('Added is-collapsed class');
        // Re-enable hover effects after transition completes
        setTimeout(() => {
          quizApp?.classList.remove('is-transitioning');
        }, 350); // Slightly longer than transition duration
      }
    }

    // Delay before advancing
    setTimeout(() => {
      setCurrentQuestion(nextIndex);
      scrollToQuestion(nextIndex);
      updateProgress(nextIndex, funnel.questions.length);
      questionStartTime = Date.now();
      onQuestionChange(); // Notify parent iframe to scroll/resize
    }, 300);
  } else {
    // Quiz complete - trigger final page
    completeQuiz();
    trackQuizComplete();

    // Update session with quiz completed
    const currentState = getState();
    updateSessionQuizCompleted(funnel.funnelID, currentState.session.sessionId);

    // Track to server
    const quizState = currentState.quiz;
    const totalTime = quizState.startedAt ? Date.now() - quizState.startedAt : 0;
    trackQuizCompleted({
      totalQuestions: funnel.questions.length,
      totalTime
    });

    logger.info('Quiz completed, triggering final page');

    // Dispatch event for app to handle
    window.dispatchEvent(new CustomEvent('quiz:complete'));
  }
}

/**
 * Scroll to specific question
 */
function scrollToQuestion(index: number): void {
  const slide = carousel?.children[index] as HTMLElement;
  if (slide) {
    slide.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    logger.debug('Scrolled to question', { index });
  }
}

/**
 * Update progress bar
 */
function updateProgress(currentIndex: number, total: number): void {
  const percent = ((currentIndex + 1) / total) * 100;
  document.documentElement.style.setProperty('--progress', `${percent}%`);
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Get current carousel index (for external use)
 */
export function getCurrentIndex(): number {
  return getState().quiz.currentQuestionIndex;
}
